import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useUserStore } from './user'
import type { RouteRecordRaw } from 'vue-router'
import { getUserMenuTree } from '@/services/menu'
import { http } from '@/services/request'
import type { Menu } from '@/types/menu'

/**
 * 权限类型
 */
export type PermissionCode = string

export const DEVICE_TYPE_MAINTAINER_ROLE_CODES = new Set([
  'SUPER_ADMIN',
  'admin',
  'OPERATION_ADMIN',
])

export function hasDeviceTypeMaintenanceAccess(
  isAdmin: boolean,
  isSuperAdmin: boolean,
  roleCodes: string[],
): boolean {
  return isAdmin
    || isSuperAdmin
    || roleCodes.some(roleCode => DEVICE_TYPE_MAINTAINER_ROLE_CODES.has(roleCode))
}

/** 判断后端返回的用户菜单树是否包含指定菜单编码。 */
export function hasMenuCodeAccess(menus: Menu[], menuCode: string): boolean {
  return menus.some(menu => menu.menuCode === menuCode
    || (menu.children ? hasMenuCodeAccess(menu.children, menuCode) : false))
}

/**
 * 权限状态 Store
 * 管理用户权限、动态路由、用户菜单树等
 */
export const usePermissionStore = defineStore('permission', () => {
  const userStore = useUserStore()

  // ========== 权限列表 ==========
  const permissions = ref<PermissionCode[]>([])
  const roleCodes = ref<string[]>([])
  const isSuperAdmin = ref(false)
  const permissionDataLoaded = ref(false)

  // ========== 用户菜单树（从后端获取）==========
  const userMenuTree = ref<Menu[]>([])

  // ========== 菜单加载状态 ==========
  const menuLoaded = ref(false)
  const menuLoading = ref(false)

  // ========== 路由权限 ==========
  const accessedRoutes = ref<RouteRecordRaw[]>([])

  // ========== 计算属性 ==========
  
  /**
   * 是否是管理员
   */
  const isAdmin = computed(() => {
    return userStore.userInfo?.isAdmin === '1'
  })

  const canMaintainDeviceTypes = computed(() => hasDeviceTypeMaintenanceAccess(
    isAdmin.value,
    isSuperAdmin.value,
    roleCodes.value,
  ))

  /**
   * 是否有指定权限
   */
  const hasPermission = (permission: PermissionCode): boolean => {
    // 管理员拥有所有权限
    if (isAdmin.value) {
      return true
    }
    return permissions.value.includes(permission)
  }

  /**
   * 是否有任一权限
   */
  const hasAnyPermission = (perms: PermissionCode[]): boolean => {
    if (isAdmin.value) {
      return true
    }
    return perms.some(perm => permissions.value.includes(perm))
  }

  /**
   * 是否有所有权限
   */
  const hasAllPermissions = (perms: PermissionCode[]): boolean => {
    if (isAdmin.value) {
      return true
    }
    return perms.every(perm => permissions.value.includes(perm))
  }

  // ========== 操作方法 ==========

  /**
   * 设置权限列表
   */
  const setPermissions = (perms: PermissionCode[]) => {
    permissions.value = perms
  }

  /**
   * 添加权限
   */
  const addPermission = (permission: PermissionCode) => {
    if (!permissions.value.includes(permission)) {
      permissions.value.push(permission)
    }
  }

  /**
   * 移除权限
   */
  const removePermission = (permission: PermissionCode) => {
    const index = permissions.value.indexOf(permission)
    if (index !== -1) {
      permissions.value.splice(index, 1)
    }
  }

  /**
   * 从后端获取用户权限码列表
   */
  const fetchUserPermissions = async (): Promise<PermissionCode[]> => {
    isSuperAdmin.value = false
    roleCodes.value = []
    permissionDataLoaded.value = false
    try {
      const res = await http.get<{
        permissions: string[]
        roleCodes: string[]
        isSuperAdmin: boolean
      }>('/account/permissions')
      if (res.code === 200 && res.data) {
        permissions.value = res.data.permissions
        roleCodes.value = res.data.roleCodes || []
        isSuperAdmin.value = res.data.isSuperAdmin === true
        return res.data.permissions
      }
    } catch (error) {
      console.error('Failed to fetch user permissions:', error)
    } finally {
      permissionDataLoaded.value = true
    }
    return []
  }

  /**
   * 从后端获取用户菜单树
   */
  const fetchUserMenuTree = async (): Promise<Menu[]> => {
    if (menuLoading.value) return userMenuTree.value
    
    menuLoading.value = true
    try {
      const res = await getUserMenuTree()
      if (res.code === 200 && res.data) {
        userMenuTree.value = res.data
        menuLoaded.value = true
        return res.data
      }
    } catch (error) {
      console.error('Failed to fetch user menu tree:', error)
    } finally {
      menuLoading.value = false
    }
    return []
  }

  /** 当前账号是否被后端菜单树授予指定菜单入口。 */
  const hasMenuAccess = (menuCode: string): boolean => {
    return hasMenuCodeAccess(userMenuTree.value, menuCode)
  }

  /**
   * 初始化权限数据（登录后调用）
   */
  const initPermissionData = async () => {
    await Promise.all([
      fetchUserPermissions(),
      fetchUserMenuTree()
    ])
  }

  /**
   * 设置可访问的路由
   */
  const setAccessedRoutes = (routes: RouteRecordRaw[]) => {
    accessedRoutes.value = routes
  }

  /**
   * 过滤路由（根据权限）
   */
  const filterRoutes = (routes: RouteRecordRaw[]): RouteRecordRaw[] => {
    const filteredRoutes: RouteRecordRaw[] = []

    routes.forEach(route => {
      const tmp = { ...route }
      
      // 检查是否需要管理员权限
      if (tmp.meta?.isAdmin && !isAdmin.value) {
        return
      }
      if (tmp.meta?.requiresDeviceTypeMaintenance && !canMaintainDeviceTypes.value) {
        return
      }
      if (tmp.meta?.permission && !hasPermission(tmp.meta.permission)) {
        return
      }

      // 递归过滤子路由
      if (tmp.children) {
        tmp.children = filterRoutes(tmp.children)
      }

      filteredRoutes.push(tmp)
    })

    return filteredRoutes
  }

  /**
   * 清空权限
   */
  const clearPermissions = () => {
    permissions.value = []
    roleCodes.value = []
    isSuperAdmin.value = false
    permissionDataLoaded.value = false
    accessedRoutes.value = []
    userMenuTree.value = []
    menuLoaded.value = false
  }

  return {
    // 状态
    permissions,
    roleCodes,
    isSuperAdmin,
    permissionDataLoaded,
    accessedRoutes,
    userMenuTree,
    menuLoaded,
    menuLoading,
    isAdmin,
    canMaintainDeviceTypes,
    
    // 方法
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    setPermissions,
    addPermission,
    removePermission,
    setAccessedRoutes,
    filterRoutes,
    clearPermissions,
    fetchUserPermissions,
    fetchUserMenuTree,
    hasMenuAccess,
    initPermissionData,
  }
})
